<template>
  <div class="recharge">
    <div class="recharge__card">
      <RechargeCard
        @emitSlotEvent="emitSlotEvent"
        @paymentModal="paymentModal"
        :slots="slots"
        :items="items"
        origin="Recharge"
      >
        <template #slot1>
          {{ dateToString() }}
        </template>
        <template #slot2>
          <span v-if="slots?.slot_type_billing === 'Postpago'">{{ slots?.slots_invoice }}</span>
          <span v-else>{{ totalSlots }}</span>
        </template>
        <template #slot3> ${{ slots?.slot_price }} </template>
        <template #slot4> {{ slots?.slot_type_billing }} </template>
      </RechargeCard>
      <div v-if="slots?.slot_type_billing === 'Prepago'">
        <TransactionsTable ref="transactionTableRef" :type="3" title="" :showSearch="true" />
      </div>
    </div>
    <div class="recharge__summary">
      <SummaryRechargeCard
        ref="rechargeRef"
        :slots="slots"
        :totalSlots="totalSlots"
        :hasSummary="hasSummary"
        :iaPrice="slots?.summary_balance"
        @successPay="fetchSlots"
      />
    </div>
  </div>
  <a-modal v-model:open="open" title="Mis pagos" :footer="null" @ok="handleOk">
    <div class="content">
      <h3>Slots:</h3>
      <p>
        Próxima fecha de facturación: <span class="line-value">{{ dateToString() }}</span>
      </p>
      <p>
        N° de slots por facturar:
        <span class="line-value" v-if="slots?.slot_type_billing === 'Postpago'">{{ slots?.slots_invoice }}</span>
        <span class="line-value" v-else>{{ totalSlots }}</span>
      </p>
      <p>
        <span>
          Precio por slot: 
          <a-tooltip v-if="slots?.tax" placement="bottom">
            <template #title>
              Es el precio por cada slot. El IGV no se incluye, se calcula aparte.
            </template>
            <InfoCircleOutlined />
          </a-tooltip>
        </span>
        <span class="line-value">${{ slots?.slot_price }}</span>
      </p>
      <p v-if="slots?.tax">
        <span>
          IGV (18%) de slots: 
          <a-tooltip placement="bottom">
            <template #title>
              Cálculo del 18% de IGV sobre el precio por slot multiplicado por el número de slots. Es un costo adicional separado.
            </template>
            <InfoCircleOutlined />
          </a-tooltip>
        </span>
        <span class="line-value">${{ monthlyPrice * slots?.tax }}</span>
      </p>
      <p>
        Modelo de pago: <span class="line-value">{{ slots?.slot_type_billing }}</span>
      </p>
      <a-divider></a-divider>
      <div v-if="hasSummary">
        <h3>Servicios adicionales contratados:</h3>
        <p>
          {{ CompanyName }} AI: <span class="line-value">${{ slots?.summary_balance }}</span>
        </p>
        <span class="agent"
          >Activado por {{ slots?.summary_enable_by?.fullname }} el {{ getFullYear() }} a las
          {{ getTime() }} hrs.
        </span>
      </div>
    </div>
    <footer>
      <a-button @click="handleOk">Cerrar Pagos</a-button>
    </footer>
  </a-modal>
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from "vue";
import RechargeCard from "@/app/settings/components/cards/RechargeCard.vue";
import TransactionsTable from "../tables/TransactionsTable.vue";
import SummaryRechargeCard from "../cards/SummaryRechargeCard.vue";
import SlotManager from "@/app/settings/services/slots/SlotManager";
import { ISlots, IItems } from "@/@types/settings/slots";
import { integrationManager } from "@/app/integrations/services/IntegrationManager";
import dayjs from "dayjs";
import { CompanyName } from "@/app/shared/utils/constants";
import { InfoCircleOutlined } from "@ant-design/icons-vue";

const manager = new SlotManager();

const slots = ref<ISlots>();
const open = ref<boolean>(false);
const hasSummary = ref<boolean>(false);

const items: IItems[] = [
  {
    title: "Próxima fecha de facturación:",
  },
  {
    title: "N° total de slots a facturar:",
    tooltip: true,
    redirect: "slots",
  },
  {
    title: "Precio por slot:",
    tooltip: true,
  },
  {
    title: "Modelo de pago:",
  },
];

const fetchSlots = async () => {
  const response = await manager.getInfoSlots();
  console.log(response, "response");
  slots.value = response;
};

const fetchIntegration = async () => {
  const response = await integrationManager.getCustomerSetting();
  hasSummary.value = response.enable_summary;
};

const dateToString = () => {
  const parsedDate = dayjs(slots.value?.date_payment);
  const formattedDate = parsedDate.format("DD [de] MMMM [del] YYYY");
  return formattedDate;
};

const getTime = () => {
  const dateInfo = slots.value?.summary_enable_at;
  if (typeof dateInfo === "string" && !isNaN(Date.parse(dateInfo))) {
    return dayjs(new Date(dateInfo)).format("HH:mm:ss");
  }
  return "";
};

const getFullYear = () => {
  const dateInfo = slots.value?.summary_enable_at;
  if (typeof dateInfo === "string" && !isNaN(Date.parse(dateInfo))) {
    return dayjs(new Date(dateInfo)).format("DD/MM/YYYY");
  }
  return "";
};

const totalSlots = computed(
  () => (slots.value?.slots_invoice ?? 0) - (slots.value?.slot_deleted ?? 0)
);

const monthlyPrice = computed(() => {
  let slotTotal = 0;
  if (slots.value?.slot_type_billing === 'Postpago') {
    slotTotal = slots.value?.slots_invoice || 0;
  } else {
    slotTotal = totalSlots.value || 0;
  }
  // const slotTotal = slots.value?.slots_invoice || 0;
  const slotPrice = slots.value?.slot_price || 0;

  if (isNaN(slotTotal) || isNaN(slotPrice)) {
    return 0;
  }
  return slotTotal * slotPrice;
});

onMounted(() => {
  fetchSlots();
  fetchIntegration();
});

const emit = defineEmits(["emitSlotEvent"]);

const emitSlotEvent = (message: string) => {
  emit("emitSlotEvent", message);
};

const handleOk = (e: MouseEvent) => {
  console.log(e);
  open.value = false;
};

const paymentModal = (message: boolean) => {
  open.value = message;
};
</script>

<style lang="sass" scoped>
.recharge
  display: flex
  flex-direction: row
  gap: 40px
  height: 100%

  &__summary
    width: 27%
  &__card
    width: 73%
.content
  padding: 10px
  p
    display: flex
    justify-content: space-between
    .line-value
      font-weight: 600
      color: $gray-8
  .agent
    color: $gray-6
    font-size: $margin-small
footer
  padding: 20px 0 10px
  display: flex
  justify-content: center
</style>
